import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppHistoryRow, AppSendHistory } from "./AppSendHistory";
import { AppSignUpApi, AppSendLine } from "./AppSignUpApi";

// AppChatRules - which sent messages belong in the open conversation until the next rebuild writes
// them into the page, and what their status line says (owner, 2026-09-25: "Sending a message should
// update the chat history directly for that recipient").
//
// ⚠ RULES ONLY - no DOM, no `window`: the Bridge suite may import this beside `AppSendHistory`.
// The act is `AppChatAppend`.
//
// ⛔ A MESSAGE NEVER SHOWS TWICE. Three guards, each for a different moment:
//   1. the rebuild has it - its WhatsApp id is on a bubble (`data-mid`), so the row is hidden;
//   2. this visit already appended it (`data-wac-id`, the request id) - the live send and the
//      history answer can both arrive;
//   3. it is OLDER than the page's newest message - the rebuild has already covered that span, and
//      a page built before `data-mid` existed cannot be deduplicated by id.
// ⛔ A REFUSED SEND IS NEVER DRAWN AS A BUBBLE: it did not happen, and a bubble says it did.

/** What the small line under an appended bubble says. `Bad` paints it in the alarm colour. */
export class AppChatWord {
    public Text: string = "";
    public Bad: boolean = false;
}

export class AppChatRules {
    public static readonly Sending: string = "Sending…";
    public static readonly Sent: string = "Sent";
    public static readonly Unconfirmed: string = "Not confirmed - check your phone";
    public static readonly NotSent: string = "Not sent";
    public static readonly Waiting: string = "Waiting to send";
    /** Must equal `AppTranslate.FailedLine` (pinned by `AppTranslate.test.ts`). */
    public static readonly TranslateFailed: string = "Not sent - couldn't translate";

    /** `m-YYYYMMDDHHMM` or `m-YYYYMMDDHHMM-2` -> the 12 digits of the UTC minute; "" otherwise. */
    public static MinuteOfId = (Id: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Found: RegExpExecArray | null = /^m-(\d{12})(-\d+)?$/.exec(nz(Id));
            if (Found === null) { return ""; }
            return Found[1];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.MinuteOfId");
            return "";
        }
    };

    /** An ISO time -> the 12 digits of its UTC minute, the page's own anchor arithmetic; "" if unreadable. */
    public static MinuteOfIso = (Iso: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: number = Date.parse(nz(Iso));
            if (isNaN(At) === true) { return ""; }
            return new Date(At).toISOString().substring(0, 16).replace(/[^0-9]/g, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.MinuteOfIso");
            return "";
        }
    };

    /** The newest minute among the page's bubble ids, or "" for a page with none. */
    public static Newest = (Ids: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Best: string = "";
            for (const Id of Ids) {
                const Minute: string = AppChatRules.MinuteOfId(Id);
                if (Minute > Best) { Best = Minute; }
            }
            return Best;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.Newest");
            return "";
        }
    };

    /**
     * The history rows to draw in this conversation, OLDEST FIRST (the order they are appended in).
     * `LastMinute` is the page's newest bubble; `OnPage` the `data-mid`s; `Appended` the request ids
     * this visit already drew.
     */
    public static Keep = (Rows: AppHistoryRow[], ChatId: string, LastMinute: string, OnPage: string[],
                          Appended: string[]): AppHistoryRow[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Chat: string = nz(ChatId).trim();
            const Out: AppHistoryRow[] = [];
            if (Chat.length === 0) { return Out; }
            for (const Row of Rows) {
                if (nz(Row.ChatId).trim() !== Chat) { continue; }
                if (Row.State === AppSendHistory.Refused) { continue; }
                // ⛔ A REACTION IS A BADGE, NEVER A BUBBLE (`ReactionBook`) - not even "Reacted 👍".
                if (nz(Row.ReactTo).length > 0) { continue; }
                const Minute: string = AppChatRules.MinuteOfIso(Row.At);
                if (Minute.length === 0 || Minute < nz(LastMinute)) { continue; }
                if (nz(Row.MessageId).length > 0 && OnPage.indexOf(Row.MessageId) >= 0) { continue; }
                if (Appended.indexOf(Row.Id) >= 0 || AppChatRules.Holds(Out, Row.Id) === true) { continue; }
                Out.push(Row);
            }
            Out.sort(AppChatRules.Older);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.Keep");
            return [];
        }
    };

    /**
     * DOCUMENTED EXCEPTION: a membership test over values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw; `Keep` is guarded.
     */
    private static Holds = (Rows: AppHistoryRow[], Id: string): boolean => {
        for (const Row of Rows) {
            if (Row.Id === Id) { return true; }
        }
        return false;
    };

    /**
     * Oldest first by time; the id breaks a tie so the order is stable.
     * DOCUMENTED EXCEPTION: a sort comparator over two values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw; `Keep` is guarded.
     */
    private static Older = (A: AppHistoryRow, B: AppHistoryRow): number => {
        const Ta: number = Date.parse(A.At);
        const Tb: number = Date.parse(B.At);
        if (Ta !== Tb) { return Ta - Tb; }
        if (A.Id < B.Id) { return -1; }
        if (A.Id > B.Id) { return 1; }
        return 0;
    };

    /** The text a history row carries: the whole message, or its preview from an older service. */
    public static TextOf = (Row: AppHistoryRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Row.Text).length > 0) { return nz(Row.Text); }
            return nz(Row.Preview);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.TextOf");
            return "";
        }
    };

    /** A history row's state, in the words of the Sent tab (`AppSendHistory.LineOf`). */
    public static WordForState = (State: string): AppChatWord => {
        if (getCoreUtils().KErrorMode === true) { return new AppChatWord(); }
        try {
            const Out: AppChatWord = new AppChatWord();
            Out.Text = AppChatRules.Waiting;
            if (State === AppSendHistory.Sent) { Out.Text = AppChatRules.Sent; }
            if (State === AppSendHistory.Unconfirmed) { Out.Text = AppChatRules.Unconfirmed; }
            if (State === AppSendHistory.Refused) { Out.Text = AppChatRules.NotSent; Out.Bad = true; }
            // ⛔ TRANSLATION: nothing was sent - the bubble also offers Retry / Send in English (`AppTranslateBubble.Failed`).
            if (State === AppSendHistory.TranslateFailed) { Out.Text = AppChatRules.TranslateFailed; Out.Bad = true; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.WordForState");
            return new AppChatWord();
        }
    };

    /**
     * A live poll's answer, decided by the SAME rule as the line under the box
     * (`AppSignUpApi.SendLineFor`): sent only on the receipt, unconfirmed is ink and says to check
     * the phone, and only a refusal is "Not sent". Anything still in flight is "Sending…".
     */
    public static WordForPoll = (Sent: boolean, Unconfirmed: boolean, Final: boolean,
                                 Why: string): AppChatWord => {
        if (getCoreUtils().KErrorMode === true) { return new AppChatWord(); }
        try {
            const Out: AppChatWord = new AppChatWord();
            Out.Text = AppChatRules.Sending;
            if (Sent !== true && Unconfirmed !== true && Final !== true) { return Out; }
            const Line: AppSendLine = AppSignUpApi.SendLineFor(Sent, Unconfirmed, Why);
            if (Sent === true) { Out.Text = AppChatRules.Sent; return Out; }
            if (Unconfirmed === true) { Out.Text = AppChatRules.Unconfirmed; return Out; }
            Out.Text = AppChatRules.NotSent;
            Out.Bad = Line.Trouble === true || Line.Text.length === 0;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.WordForPoll");
            return new AppChatWord();
        }
    };

    /**
     * A UTC minute (`YYYYMMDDHHMM`, a bubble id's digits) -> the READER's day `YYYYMMDD` (the browser's zone, which is
     * the zone the page's days are drawn in - `/folder` sends it). "" for anything unreadable.
     */
    public static LocalDay = (Minute: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const M: string = nz(Minute);
            if (/^[0-9]{12}$/.test(M) === false) { return ""; }
            const At: number = Date.UTC(Number(M.substring(0, 4)), Number(M.substring(4, 6)) - 1, Number(M.substring(6, 8)),
                Number(M.substring(8, 10)), Number(M.substring(10, 12)));
            return AppChatRules.DayKey(new Date(At));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.LocalDay");
            return "";
        }
    };

    /** A moment's `YYYYMMDD` on the reader's own clock. */
    public static DayKey = (When: Date, Separator: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return String(When.getFullYear()) + Separator + String(When.getMonth() + 1).padStart(2, "0")
                + Separator + String(When.getDate()).padStart(2, "0");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.DayKey");
            return "";
        }
    };

    /** The pill over a day this page adds: "Today", or the date. `Key` is `YYYYMMDD` on the reader's clock. */
    public static DayCaption = (Key: string, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Today: string = AppChatRules.DayKey(new Date(NowMs));
            if (Key === Today) { return "Today"; }
            const At: number = Date.parse(Key.substring(0, 4) + "-" + Key.substring(4, 6) + "-"
                + Key.substring(6, 8) + "T12:00:00Z");
            if (isNaN(At) === true) { return ""; }
            return new Date(At).toLocaleDateString(undefined, { day: "numeric", month: "short" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatRules.DayCaption");
            return "";
        }
    };
}
